import moment from 'moment'
import i18n from '@/libs/i18n'
import { formatPlanningMinutes } from '@/helpers/orderPlanning'

export const BACKLOG_RESOURCE_ID = 'planner-backlog'
export const BACKLOG_RESOURCE_NAME = 'Nie zaplanowane'
export const REALTIME_REFRESH_DELAY_MS = 350
export const DEFAULT_PLANNED_DURATION_MINUTES = 60
export const DEFAULT_CREATE_DURATION_MINUTES = 60
export const PLANNER_ORDER_TYPES = [
  'basic_transport',
  'specialized_transport',
  'sanitary_transport',
  'multi_transport',
  'medical_courier',
  'event',
]

const PLANNER_DATE_FORMAT = 'YYYY-MM-DD'
const PLANNER_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss'
const HTML_ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

export const normalizePlannerDate = value => {
  if (moment(value, PLANNER_DATE_FORMAT, true).isValid()) {
    return moment(value, PLANNER_DATE_FORMAT, true).format(PLANNER_DATE_FORMAT)
  }

  return moment().format(PLANNER_DATE_FORMAT)
}

export const createPlannerResources = teamDefinitions => (
  Array.isArray(teamDefinitions)
    ? teamDefinitions.map(teamDefinition => ({
      id: String(teamDefinition.id),
      name: teamDefinition.code,
      original: teamDefinition,
    }))
    : []
)

export const resolvePlannerEventColor = type => {
  switch (type) {
    case 'event':
      return '#f59e0b'
    case 'multi_transport':
      return '#2563eb'
    case 'medical_courier':
      return '#4f46e5'
    default:
      return '#059669'
  }
}

export const escapePlannerHtml = value => String(value ?? '').replace(/[&<>"']/g, character => HTML_ESCAPE_MAP[character])

export const resolvePlannerAddressLabel = address => {
  if (!address || typeof address !== 'object') {
    return '-'
  }

  return address.alias || address.name || [address.city, address.address].filter(Boolean).join(', ') || '-'
}

const formatPlannerDateTime = value => moment.parseZone(value).format(PLANNER_DATE_TIME_FORMAT)

const resolvePlannerDurationMinutes = order => {
  const value = Number(order?.plannedDurationMinutes || DEFAULT_PLANNED_DURATION_MINUTES)
  return value > 0 ? value : DEFAULT_PLANNED_DURATION_MINUTES
}

const resolvePlannerAssignedState = order => {
  if (order?.isAssigned !== undefined || order?.assigned !== undefined) {
    return Boolean(order?.isAssigned ?? order?.assigned)
  }

  const hasAssignedTeamWithoutPlanning = order?.plannedTeamDefinition
    && order?.plannedAssignmentOffsetMinutes == null
    && order?.plannedDurationMinutes == null

  return Boolean(hasAssignedTeamWithoutPlanning)
}

const resolveStartMoment = order => {
  const startAt = moment.parseZone(order?.startAt)
  return startAt.isValid() ? startAt : null
}

export const resolveBufferBeforeMinutes = order => {
  const plannedAssignmentOffsetMinutes = Number(order?.plannedAssignmentOffsetMinutes)
  return plannedAssignmentOffsetMinutes > 0 ? plannedAssignmentOffsetMinutes : 0
}

export const resolvePlannerEventStart = order => {
  const startAt = resolveStartMoment(order)

  if (!startAt) {
    return null
  }

  return formatPlannerDateTime(startAt)
}

export const resolvePlannerEventEnd = order => {
  const startAt = resolveStartMoment(order)
  if (!startAt) {
    return null
  }

  return startAt
    .clone()
    .add(resolvePlannerDurationMinutes(order), 'minutes')
    .format(PLANNER_DATE_TIME_FORMAT)
}

export const createPlannerEventText = order => [
  order.fullNumber || order.type,
  resolvePlannerAddressLabel(order.pickupAddress),
  resolvePlannerAddressLabel(order.deliveryAddress),
].filter(Boolean).join(' | ')

export const mapPlannerOrderToEvent = order => {
  const isFirstAvailable = Boolean(order?.isFirstAvailablePlanning)
  const teamDefinitionId = order.plannedTeamDefinition?.id
  const eventId = isFirstAvailable && teamDefinitionId
    ? `${order.id}-${teamDefinitionId}`
    : String(order.id)
  const isAssigned = resolvePlannerAssignedState(order)

  return {
    id: eventId,
    orderId: order.id,
    start: resolvePlannerEventStart(order),
    end: resolvePlannerEventEnd(order),
    title: order.fullNumber || order.type,
    resource: teamDefinitionId ? String(teamDefinitionId) : undefined,
    color: resolvePlannerEventColor(order.type),
    cssClass: [
      'order-planner-calendar-scheduler__event-host',
      'order-planner-calendar-timeline__event-host',
      isAssigned ? 'order-planner-calendar-scheduler__event-host--assigned' : '',
      isAssigned ? 'order-planner-calendar-timeline__event-host--assigned' : '',
    ].filter(Boolean).join(' '),
    editable: !isAssigned,
    bufferBefore: resolveBufferBeforeMinutes(order),
    dragInTime: false,
    resize: false,
    original: {
      ...order,
      orderId: order.id,
      orderNumber: order.fullNumber,
      durationMinutes: resolvePlannerDurationMinutes(order),
      bufferBefore: resolveBufferBeforeMinutes(order),
      assignmentOffsetLabel: formatPlanningMinutes(resolveBufferBeforeMinutes(order)),
      eventText: createPlannerEventText(order),
      color: resolvePlannerEventColor(order.type),
      isAssigned,
      isFirstAvailablePlanning: isFirstAvailable,
    },
  }
}

export const mapPlannerOrderToBacklogItem = order => ({
  id: String(order.id),
  title: order.fullNumber || order.type,
  start: resolvePlannerEventStart(order),
  end: resolvePlannerEventEnd(order),
  color: resolvePlannerEventColor(order.type),
  original: {
    ...order,
    orderId: order.id,
    orderNumber: order.fullNumber,
    durationMinutes: resolvePlannerDurationMinutes(order),
    bufferBefore: resolveBufferBeforeMinutes(order),
    assignmentOffsetLabel: formatPlanningMinutes(resolveBufferBeforeMinutes(order)),
    eventText: createPlannerEventText(order),
    color: resolvePlannerEventColor(order.type),
  },
})

export const mapInternalEventToSyncfusion = event => {
  const isAssigned = event.original?.isAssigned || false

  return {
    Id: event.id,
    Subject: event.title || event.original?.orderNumber || '',
    StartTime: event.start ? new Date(event.start) : null,
    EndTime: event.end ? new Date(event.end) : null,
    TeamId: event.resource || undefined,
    Color: event.color,
    IsReadonly: isAssigned,
    IsAssigned: isAssigned,
    IsFirstAvailablePlanning: event.original?.isFirstAvailablePlanning || false,
    IsBuffer: false,
    DragInTime: !isAssigned,
    AllowResize: !isAssigned,
    BufferBeforeMinutes: event.original?.bufferBefore || 0,
    Original: event.original || {},
  }
}

export const createTimelineCompositeEvents = sfEvents => sfEvents.map(event => {
  if (event.IsBuffer || event.BufferBeforeMinutes <= 0 || !event.StartTime) {
    return event
  }

  const visualStart = new Date(event.StartTime)
  const compositeStart = new Date(visualStart.getTime() - event.BufferBeforeMinutes * 60 * 1000)

  return {
    ...event,
    StartTime: compositeStart,
    VisualStartTime: visualStart,
    IsTimelineComposite: true,
  }
})

const resolveSyncfusionEventActualStart = sfEvent => {
  if (!sfEvent?.StartTime) {
    return null
  }

  if (sfEvent.IsTimelineComposite) {
    return moment(sfEvent.StartTime)
      .add(Number(sfEvent.Original?.bufferBefore || sfEvent.BufferBeforeMinutes || 0), 'minutes')
      .format(PLANNER_DATE_TIME_FORMAT)
  }

  return moment(sfEvent.StartTime).format(PLANNER_DATE_TIME_FORMAT)
}

export const mapSyncfusionEventToInternal = sfEvent => ({
  id: sfEvent?.Id,
  orderId: sfEvent?.Original?.orderId || sfEvent?.Original?.id,
  start: resolveSyncfusionEventActualStart(sfEvent),
  end: sfEvent?.EndTime ? moment(sfEvent.EndTime).format(PLANNER_DATE_TIME_FORMAT) : null,
  resource: sfEvent?.TeamId || null,
  original: sfEvent?.Original || {},
})

export const mapPlannerResponseToState = payload => {
  const safePayload = payload || {}
  const date = normalizePlannerDate(safePayload.date)
  const unplannedOrders = Array.isArray(safePayload.unplannedOrders) ? safePayload.unplannedOrders : []
  const plannedOrders = Array.isArray(safePayload.plannedOrders) ? safePayload.plannedOrders : []
  const teamDefinitions = Array.isArray(safePayload.teamDefinitions) ? safePayload.teamDefinitions : []

  return {
    date,
    teamDefinitions,
    unplannedOrders,
    plannedOrders,
    resources: createPlannerResources(teamDefinitions),
    backlogItems: unplannedOrders.map(mapPlannerOrderToBacklogItem),
    events: plannedOrders.map(mapPlannerOrderToEvent),
  }
}

export const createPlannerQuery = ({ date, types }) => ({
  date: normalizePlannerDate(date),
  types: Array.isArray(types) && types.length > 0 && types.length < PLANNER_ORDER_TYPES.length ? types : null,
})

export const resolveEventDurationMinutes = event => {
  const start = moment(event?.start)
  const end = moment(event?.end)
  const resolvedDurationMinutes = end.diff(start, 'minutes')

  return resolvedDurationMinutes > 0
    ? resolvedDurationMinutes
    : Number(event?.original?.durationMinutes || DEFAULT_PLANNED_DURATION_MINUTES)
}

export const createPlanningPayloadFromEvent = event => {
  const resource = String(event?.resource || '')
  if (!resource) {
    return null
  }

  return {
    teamDefinition: Number(resource),
    assignmentOffsetMinutes: Number(event?.original?.bufferBefore || 0),
    durationMinutes: resolveEventDurationMinutes(event),
  }
}

export const createOrderPatchPayloadFromEvent = event => {
  const startAt = moment(event?.start)
  if (!startAt.isValid()) {
    return null
  }

  return {
    startAt: startAt.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
    plannedAssignmentOffsetMinutes: Number(event?.original?.bufferBefore || 0),
    plannedDurationMinutes: resolveEventDurationMinutes(event),
    plannedTeamDefinition: event?.resource ? { id: Number(event.resource) } : null,
  }
}

export const createDurationPatchPayload = ({ event, durationMinutes }) => {
  const normalizedDurationMinutes = Number(durationMinutes)
  if (!Number.isFinite(normalizedDurationMinutes) || normalizedDurationMinutes <= 0) {
    return null
  }

  return {
    plannedAssignmentOffsetMinutes: Number(event?.original?.bufferBefore || 0),
    plannedDurationMinutes: normalizedDurationMinutes,
    plannedTeamDefinition: event?.resource ? { id: Number(event.resource) } : null,
  }
}

export const createAssignmentOffsetPatchPayload = ({ event, assignmentOffsetMinutes }) => {
  const normalizedAssignmentOffsetMinutes = Number(assignmentOffsetMinutes)
  if (!Number.isFinite(normalizedAssignmentOffsetMinutes) || normalizedAssignmentOffsetMinutes < 0) {
    return null
  }

  return {
    plannedAssignmentOffsetMinutes: normalizedAssignmentOffsetMinutes,
    plannedDurationMinutes: resolveEventDurationMinutes(event),
    plannedTeamDefinition: event?.resource ? { id: Number(event.resource) } : null,
  }
}

export const createPlannerCreateQuery = ({
  resourceId,
  start,
}) => ({
  plannerTeamDefinitionId: resourceId ? String(resourceId) : undefined,
  plannerStartAt: moment(start).format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
})

export const hasPlannerStartAtChanged = ({ order, nextStartAt }) => {
  const currentStartAt = moment.parseZone(order?.startAt)
  const normalizedNextStartAt = moment.parseZone(nextStartAt)

  if (!currentStartAt.isValid() || !normalizedNextStartAt.isValid()) {
    return false
  }

  return currentStartAt.valueOf() !== normalizedNextStartAt.valueOf()
}

export const createPlannerOrderWithOverrides = ({
  order,
  teamDefinitions,
  teamDefinitionId,
  startAt,
  assignmentOffsetMinutes,
  durationMinutes,
}) => {
  const resolvedTeamDefinitionId = Number(teamDefinitionId || order?.plannedTeamDefinition?.id || 0)
  const resolvedTeamDefinition = Array.isArray(teamDefinitions)
    ? teamDefinitions.find(teamDefinition => Number(teamDefinition?.id) === resolvedTeamDefinitionId) || null
    : null

  return {
    ...order,
    startAt: startAt || order?.startAt,
    plannedAssignmentOffsetMinutes: Number(assignmentOffsetMinutes),
    plannedDurationMinutes: Number(durationMinutes),
    plannedTeamDefinition: resolvedTeamDefinition || order?.plannedTeamDefinition || null,
  }
}

export const createReadablePlannerDate = value => moment(value, PLANNER_DATE_FORMAT).locale('pl').format('dddd, D MMMM YYYY')

export const createReadablePlannerTime = value => {
  const parsedValue = moment.parseZone(value)
  return parsedValue.isValid() ? parsedValue.format('HH:mm') : '-'
}

export const createReadablePlannerDateTime = value => {
  const parsedValue = moment.parseZone(value)
  return parsedValue.isValid() ? parsedValue.format('YYYY-MM-DD HH:mm') : '-'
}

export const createReadablePlannerScheduledAt = value => {
  const parsedValue = moment.parseZone(value)
  return parsedValue.isValid() ? parsedValue.format('DD.MM.YYYY HH:mm') : '-'
}

export const createReadablePlannerEventDateRange = ({ start, end, durationMinutes }) => {
  const startAt = moment.parseZone(start)
  const normalizedDurationMinutes = Number(durationMinutes)

  if (normalizedDurationMinutes > 0) {
    const endAt = startAt.clone().add(normalizedDurationMinutes, 'minutes')
    return `${startAt.format('HH:mm')} - ${endAt.format('HH:mm')}`
  }

  const endAt = moment.parseZone(end)

  if (!endAt.isValid()) {
    return startAt.format('HH:mm')
  }

  return `${startAt.format('HH:mm')} - ${endAt.format('HH:mm')}`
}

const resolvePlannerRouteAddressText = address => {
  if (!address || typeof address !== 'object') {
    return '-'
  }

  const alias = address.alias || ''
  const name = address.name || ''
  const addressLine = [address.city, address.address].filter(Boolean).join(', ')

  return [alias, name, addressLine].filter(Boolean).join(' | ') || '-'
}

const createPlannerRouteTooltip = original => (
  `Odbior: ${resolvePlannerRouteAddressText(original?.pickupAddress)}\nCel: ${resolvePlannerRouteAddressText(original?.deliveryAddress)}`
)

export const createPlannerPatientsLabel = original => {
  const patients = original?.patients
    || (Array.isArray(original?.patientInfo)
      ? original.patientInfo.map(patient => ({ name: patient.name, surname: patient.surname }))
      : [])

  if (!Array.isArray(patients) || patients.length === 0) {
    return ''
  }

  return patients
    .map(patient => [patient.surname, patient.name].filter(Boolean).join(' '))
    .filter(Boolean)
    .join(', ')
}

export const resolvePlannerOrderStatusLabel = status => {
  if (!status) {
    return ''
  }

  const translationKey = `order.status.${status}`
  const translatedStatus = String(i18n.t(translationKey))

  return translatedStatus !== translationKey ? translatedStatus : String(status)
}

export const createPlannerSchedulerEventViewModel = event => {
  const original = event?.original || {}
  const nestedOriginal = original?.original || {}
  const eventData = {
    ...nestedOriginal,
    ...original,
    ...event,
    startAt: event?.startAt || original.startAt || nestedOriginal.startAt || event?.start,
    endAt: event?.endAt || original.endAt || nestedOriginal.endAt || event?.end,
    durationMinutes: event?.durationMinutes
      || original.durationMinutes
      || nestedOriginal.durationMinutes
      || event?.plannedDurationMinutes
      || original.plannedDurationMinutes
      || nestedOriginal.plannedDurationMinutes,
    customerName: event?.customerName || original.customerName || nestedOriginal.customerName || event?.customerAddress?.customer?.name || original.customerAddress?.customer?.name || nestedOriginal.customerAddress?.customer?.name || '',
    patients: event?.patients || original.patients || nestedOriginal.patients,
    patientInfo: event?.patientInfo || original.patientInfo || nestedOriginal.patientInfo,
    pickupAddress: event?.pickupAddress || original.pickupAddress || nestedOriginal.pickupAddress,
    deliveryAddress: event?.deliveryAddress || original.deliveryAddress || nestedOriginal.deliveryAddress,
    returnTransport: event?.returnTransport ?? original.returnTransport ?? nestedOriginal.returnTransport,
    prioritySignals: event?.prioritySignals ?? original.prioritySignals ?? nestedOriginal.prioritySignals,
    absolutePunctuality: event?.absolutePunctuality ?? original.absolutePunctuality ?? nestedOriginal.absolutePunctuality,
    infectious: event?.infectious ?? original.infectious ?? nestedOriginal.infectious,
    private: event?.private ?? original.private ?? nestedOriginal.private,
    status: event?.status || original.status || nestedOriginal.status || '',
    fullNumber: event?.fullNumber || original.fullNumber || nestedOriginal.fullNumber || event?.title || original.orderNumber || nestedOriginal.orderNumber,
    orderNumber: event?.orderNumber || original.orderNumber || nestedOriginal.orderNumber,
    orderId: event?.orderId || original.orderId || nestedOriginal.orderId || event?.id || original.id || nestedOriginal.id || '',
  }

  return {
    orderId: eventData.orderId,
    orderPath: `/order/view/${escapePlannerHtml(eventData.orderId)}`,
    title: eventData.orderNumber || eventData.fullNumber || eventData.type || '-',
    statusLabel: resolvePlannerOrderStatusLabel(eventData.status),
    scheduledAtLabel: createReadablePlannerEventDateRange({
      start: eventData.startAt,
      end: eventData.endAt,
      durationMinutes: eventData.durationMinutes,
    }),
    customerName: eventData.customerName,
    patientsLabel: createPlannerPatientsLabel(eventData),
    pickupAddressLabel: resolvePlannerAddressLabel(eventData.pickupAddress),
    deliveryAddressLabel: resolvePlannerAddressLabel(eventData.deliveryAddress),
    routeTooltip: createPlannerRouteTooltip(eventData),
    returnTransport: Boolean(eventData.returnTransport),
    prioritySignals: Boolean(eventData.prioritySignals),
    absolutePunctuality: Boolean(eventData.absolutePunctuality),
    infectious: Boolean(eventData.infectious),
    private: Boolean(eventData.private),
    isAssigned: Boolean(eventData.isAssigned),
    isFirstAvailablePlanning: Boolean(eventData.isFirstAvailablePlanning),
  }
}

export const createRealtimeRefreshScheduler = refresh => {
  let timeoutId = null

  return () => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      refresh()
    }, REALTIME_REFRESH_DELAY_MS)
  }
}

export const IN_PROGRESS_ORDER_STATUSES = [
  'confirmed',
  'shipment_received',
  'shipment_delivered',
  'on_way_to_pickup_point',
  'at_pickup_point',
  'patient_pick_up',
  'on_way_to_delivery_point',
  'at_delivery_point',
  'waiting_for_return_pickup',
  'return_patient_pick_up',
  'on_way_to_return_delivery_point',
  'at_return_delivery_point',
  'patient_delivery',
  'patient_not_taken',
  'patient_left_at_destination',
]

export const DONE_ORDER_STATUSES = ['completed', 'done']

export const normalizeComparableDateValue = value => {
  const normalizedMoment = moment(value)
  if (normalizedMoment.isValid()) {
    return normalizedMoment.valueOf()
  }
  return String(value || '')
}

const createComparablePlannerEventState = event => ({
  resource: String(event?.resource || ''),
  start: normalizeComparableDateValue(event?.start),
  end: normalizeComparableDateValue(event?.end),
  bufferBefore: Number(event?.original?.bufferBefore || 0),
  durationMinutes: resolveEventDurationMinutes(event),
})

export const eventHasMeaningfulChange = ({ event, oldEvent }) => JSON.stringify(
  createComparablePlannerEventState(event),
) !== JSON.stringify(
  createComparablePlannerEventState(oldEvent),
)

export const eventHasTimeChange = ({ event, oldEvent }) => {
  if (!event || !oldEvent) {
    return false
  }
  return normalizeComparableDateValue(event.start) !== normalizeComparableDateValue(oldEvent.start)
    || normalizeComparableDateValue(event.end) !== normalizeComparableDateValue(oldEvent.end)
}

export const uniqueNumericIds = values => [...new Set(
  (Array.isArray(values) ? values : [])
    .map(value => Number(value))
    .filter(Boolean),
)]

export const resolvePlannerDateQuery = value => {
  if (!value) {
    return null
  }
  const normalizedValue = normalizePlannerDate(value)
  return normalizedValue === value ? normalizedValue : null
}

export const resolveAutomaticPlannerViewMode = resourcesCount => (resourcesCount > 5 ? 'timeline' : 'scheduler')

export const hasPlannerViewModeQuery = routeQuery => ['scheduler', 'timeline'].includes(routeQuery?.view)
